import type { ListingDetails } from 'app/types/listingDetails.type';

export type RentalTypeBadgeKey =
  | 'roomForRent'
  | 'sublet'
  | 'furnished'
  | 'incomeRestricted'
  | 'seniorHousing'
  | 'studentHousing'
  | 'corporate'
  | 'militaryHousing';

export type RentalTypeBadge = {
  key: RentalTypeBadgeKey;
  label: string;
};

// `details.furnished | studentHousing | seniorHousing | militaryHousing` is the
// canonical path for a full HDP listing (parsed in `shared/models/Details.ts`);
// `listingType` lives at the top level. `incomeRestricted` follows the legacy
// rule: derived from `contact.restricted` and excluded for buildings.
//
// `militaryHousing` is also accepted at the top level as a fallback for
// SummaryV2-shaped data (which sets it on the listing root, not on `details`).
//
// BDPs are backed by `BuildingV2`, which only sets `details.title` on the
// building itself and stores rental attributes on the underlying units. For
// buildings, derive badges from `units[0]` so the BDP reflects what's actually
// for rent in that building. The `incomeRestricted` exclusion still keys off
// the original listing's `building` flag — the building isn't itself
// income-restricted; only its individual units are.
export const getRentalTypeBadges = (listing: ListingDetails | null | undefined): Array<RentalTypeBadge> => {
  if (!listing) return [];

  const isBuilding = Boolean(listing.building);
  // Local widening for the BuildingV2 runtime shape. `ListingDetails` doesn't
  // declare `units` because the canonical type is hand-maintained against the
  // API; see the TODO(listing-types) note at the top of `listingDetails.type.ts`
  // for the longer-term cleanup path (OpenAPI-driven generation).
  const withUnits = listing as ListingDetails & { units?: Array<ListingDetails> };
  const source: ListingDetails =
    isBuilding && withUnits.units && withUnits.units.length > 0 ? withUnits.units[0] : listing;

  const badges: Array<RentalTypeBadge> = [];

  if (source.listingType === 'room') {
    badges.push({ key: 'roomForRent', label: 'Room for rent' });
  }
  if (source.listingType === 'sublet') {
    badges.push({ key: 'sublet', label: 'Sublet' });
  }
  if (source.details?.furnished) {
    badges.push({ key: 'furnished', label: 'Furnished' });
  }
  if (!isBuilding && source.contact?.restricted) {
    badges.push({ key: 'incomeRestricted', label: 'Income restricted' });
  }
  if (source.details?.seniorHousing) {
    badges.push({ key: 'seniorHousing', label: 'Senior housing' });
  }
  if (source.details?.studentHousing) {
    badges.push({ key: 'studentHousing', label: 'Student housing' });
  }
  if (source.listingType === 'corporate') {
    badges.push({ key: 'corporate', label: 'Corporate housing' });
  }
  if (source.militaryHousing || source.details?.militaryHousing) {
    badges.push({ key: 'militaryHousing', label: 'Military housing' });
  }

  return badges;
};
